import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  Box
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  DocumentSourceProviderConfigResponse,
  DocumentSourceUserConnectionRequest,
  DocumentSourceUserConnectionResponse,
} from '../../../api/ApiClient';

type FormDataType = Omit<DocumentSourceUserConnectionRequest, 'provider_config_id'> & { provider_config_id: string };

interface ConnectionFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: DocumentSourceUserConnectionRequest) => Promise<void>;
  availableProviders: DocumentSourceProviderConfigResponse[];
  editingConnection: DocumentSourceUserConnectionResponse | null;
  formData: FormDataType;
  onFormChange: (data: Partial<FormDataType>) => void;
}

export const ConnectionFormDialog: React.FC<ConnectionFormDialogProps> = ({
  open,
  onClose,
  onSave,
  availableProviders,
  editingConnection,
  formData,
  onFormChange
}) => {
  const { t } = useTranslation();

  const syncIntervalOptions = [
    { value: 'manual', label: t('documentSources.syncInterval.manual') },
    { value: 'hourly', label: t('documentSources.syncInterval.hourly') },
    { value: 'daily', label: t('documentSources.syncInterval.daily') },
    { value: 'weekly', label: t('documentSources.syncInterval.weekly') },
  ];

  const handleFileFiltersChange = (value: string) => {
    const filters = value.split(',').map(f => f.trim()).filter(Boolean);
    onFormChange({ file_filters: filters });
  };

  const handleSave = async () => {
    const dataToSend: DocumentSourceUserConnectionRequest = {
      ...formData,
      provider_config_id: formData.provider_config_id,
    };
    await onSave(dataToSend);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingConnection ? t('documentSources.dialog.editTitle') : t('documentSources.dialog.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {!editingConnection && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t('documentSources.dialog.provider')}</InputLabel>
                <Select
                  value={formData.provider_config_id}
                  onChange={(e) => onFormChange({ provider_config_id: e.target.value })}
                  label={t('documentSources.dialog.provider')}
                  required
                >
                  {availableProviders.map((provider) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.name} ({provider.provider_type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('documentSources.dialog.connectionName')}
              value={formData.name}
              onChange={(e) => onFormChange({ name: e.target.value })}
              required
              helperText={t('documentSources.dialog.connectionNameHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('documentSources.dialog.folderPath')}
              value={formData.folder_path}
              onChange={(e) => onFormChange({ folder_path: e.target.value })}
              helperText={t('documentSources.dialog.folderPathHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('documentSources.dialog.shareUrl')}
              value={formData.share_url}
              onChange={(e) => onFormChange({ share_url: e.target.value })}
              helperText={t('documentSources.dialog.shareUrlHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t('documentSources.dialog.syncInterval')}</InputLabel>
              <Select
                value={formData.sync_interval}
                onChange={(e) => onFormChange({ sync_interval: e.target.value })}
                label={t('documentSources.dialog.syncInterval')}
              >
                {syncIntervalOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('documentSources.dialog.maxFileSize')}
              type="number"
              value={(formData.max_file_size || 0) / (1024 * 1024)}
              onChange={(e) => onFormChange({ max_file_size: parseInt(e.target.value) * 1024 * 1024 })}
              helperText={t('documentSources.dialog.maxFileSizeHelp')}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('documentSources.dialog.fileFilters')}
              value={formData.file_filters?.join(', ') || ''}
              onChange={(e) => handleFileFiltersChange(e.target.value)}
              helperText={t('documentSources.dialog.fileFiltersHelp')}
            />
            <Box sx={{ mt: 1 }}>
              {formData.file_filters?.map((filter, index) => (
                <Chip
                  key={index}
                  label={filter}
                  size="small"
                  onDelete={() => {
                    const newFilters = [...(formData.file_filters || [])];
                    newFilters.splice(index, 1);
                    onFormChange({ file_filters: newFilters });
                  }}
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))}
            </Box>
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) => onFormChange({ enabled: e.target.checked })}
                />
              }
              label={t('common.enabled')}
            />
          </Grid>

          <Grid size={12}>
            <Alert severity="info">
              {t('documentSources.dialog.advancedSettingsInfo')}
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {editingConnection ? t('common.update') : t('common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};